import { customElement, property } from 'lit/decorators.js';
import { CSSResult, html, LitElement, nothing, unsafeCSS } from 'lit';
import { consume } from '@lit/context';
import { WishlistAppConfig, wishlistAppConfigContext } from '../../../scripts/context/appConfigContext.js';
import { Events, Messages } from '../../../@types/wishlist.js';
import { i18nContext } from '../../../scripts/context/i18nContext.js';
import { productsContext } from '../../../scripts/context/wishlistContext.js';
import { ProductsState } from '../../../scripts/context/wishlist-state.js';
import { ProductEventAggregate } from '../../../scripts/product-events/product-event-aggregate.js';
import '../event-item/event-item.js';
import styles from './event-layer-content.scss?inline';
import { sharedCss } from '../../../scripts/style-bundle/shared.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import clockAlarm from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-24_clock-alarm.svg?element';
import user from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-24_user.svg?element';
import { addTitleToIcon } from '../icon/icon-accessibility.js';
import { eventApi } from '../../../scripts/product-events/event-api.js';

@customElement('ents-event-layer-content')
export class EventLayerContent extends LitElement {
  @consume({ context: i18nContext })
  private messages!: Messages;

  @consume({ context: wishlistAppConfigContext })
  private wishlistAppConfig!: WishlistAppConfig;

  @consume({ context: productsContext, subscribe: true })
  @property({ attribute: false })
  private productsState!: ProductsState;

  @property({ type: Boolean, attribute: 'logged-in' })
  private loggedIn: boolean = false;

  @property({ type: Boolean, attribute: 'shown' })
  private shown: boolean = false;

  @property({ type: Object, reflect: false })
  private events: Events = { unseenProductEvents: [], seenProductEvents: [] };

  @property({ type: Boolean })
  private hasProcessedEventsSeen = false;

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles)];
  }

  private getProductEventAggregates() {
    if (this.events.unseenProductEvents.length === 0 && this.events.seenProductEvents.length === 0) {
      return { unseenProductEvents: [], seenProductEvents: [] };
    }
    return this.productsState.getProductEventAggregates(this.events.unseenProductEvents, this.events.seenProductEvents);
  }

  render() {
    if (!this.loggedIn) {
      return this.renderLoginHint();
    }

    const productEventAggregates = this.getProductEventAggregates();
    if (
      productEventAggregates.unseenProductEvents.length === 0 &&
      productEventAggregates.seenProductEvents.length === 0
    ) {
      return this.renderNoUpdatesHint();
    }
    this.updateEventsSeen();
    return this.renderItems(productEventAggregates);
  }

  private updateEventsSeen() {
    setTimeout(() => {
      if (this.shown && !this.hasProcessedEventsSeen && this.events?.maxUnseenOccasionDate) {
        eventApi
          .postEventsSeenUntil(this.wishlistAppConfig.eventsSeenEndpoint, this.events.maxUnseenOccasionDate)
          .then(() => (this.hasProcessedEventsSeen = true))
          .catch((err) => console.error('Failed to update events seen:', err));
      }
    }, 100);
  }

  private renderItems(items: {
    unseenProductEvents: ProductEventAggregate[];
    seenProductEvents: ProductEventAggregate[];
  }) {
    return html`
      <div
        class="ents-wishlist-event-layer
        ${this.wishlistAppConfig.isAppNativeBottomSheetEnabled
          ? ' ents-wishlist-event-layer__native-bottom-sheet'
          : ''}"
        data-test-ents-event-layer-updates
      >
        <h2>${unsafeHTML(this.messages['ents_wishlist_event_layer_title'])}</h2>
        <div class="ents-wishlist-event-layer__scrollable">
          <p class="ents-wishlist-event-layer__hint">${unsafeHTML(this.messages['ents_wishlist_event_layer_hint'])}</p>
          ${this.renderItemList('unseen', items.unseenProductEvents)}
          ${this.renderItemList('seen', items.seenProductEvents, true)}
        </div>
      </div>
    `;
  }

  private renderItemList(type: string, productEventAggregates: ProductEventAggregate[], alreadySeen: boolean = false) {
    if (productEventAggregates.length === 0) {
      return nothing;
    }
    const titleMsgKey = `ents_wishlist_event_${type}_title`;
    return html`
      <h3 class="ents-wishlist-event-layer__title" data-test-ents-event-title="${type}">
        ${unsafeHTML(this.messages[titleMsgKey] ?? '')} (${productEventAggregates.length})
      </h3>
      <div class="ents-wishlist-event-layer__items" data-test-ents-event-items="${type}">
        ${productEventAggregates.map((productEventAggregate, index) => {
          return this.renderItem(productEventAggregate, index + 1, productEventAggregates.length, alreadySeen);
        })}
      </div>
    `;
  }

  private renderItem(
    productEventAggregate: ProductEventAggregate,
    position: number,
    listCount: number,
    alreadySeen: boolean,
  ) {
    return html`
      <ents-event-item
        .productEventAggregate=${productEventAggregate}
        position=${position}
        count=${listCount}
        ?logged-in=${this.loggedIn}
        ?already-seen=${alreadySeen}
      >
      </ents-event-item>
    `;
  }

  private renderNoUpdatesHint() {
    return html`
      <div
        class="ents-wishlist-event-layer ents-wishlist-event-layer__no-updates
             ${this.wishlistAppConfig.isAppNativeBottomSheetEnabled
          ? 'ents-wishlist-event-layer__native-bottom-sheet'
          : ''}"
        data-test-ents-event-layer-no-updates
      >
        <div class="ents-wishlist-event-layer__icon ents-wishlist-event-layer__icon-clock-alarm">
          ${addTitleToIcon(clockAlarm(), this.messages['ents_wishlist_event_layer_empty_title'])}
        </div>
        <h2 class="ents-wishlist-event-layer__headline">${this.messages['ents_wishlist_event_layer_empty_title']}</h2>
        <p class="ents-copy">${this.messages['ents_wishlist_event_layer_empty_hint']}</p>
      </div>
    `;
  }

  private renderLoginHint() {
    return html`
      <div
        class="ents-wishlist-event-layer ents-wishlist-event-layer__login-hint
             ${this.wishlistAppConfig.isAppNativeBottomSheetEnabled
          ? 'ents-wishlist-event-layer__native-bottom-sheet'
          : ''}"
        data-test-ents-event-layer-login
      >
        <div class="ents-wishlist-event-layer__icon ents-wishlist-event-layer__icon-user">
          ${addTitleToIcon(user(), this.messages['ents_wishlist_event_layer_login_title'])}
        </div>
        <h2 class="ents-wishlist-event-layer__headline">${this.messages['ents_wishlist_event_layer_login_title']}</h2>
        <p class="ents-copy">
          <a class="bdk-link-inline" href="${this.wishlistAppConfig.loginEndpointWithFavoritesEventLayerOpenReferrer}">
            ${this.messages['ents_wishlist_event_layer_login_linktext']}</a
          >
          ${this.messages['ents_wishlist_event_layer_login_hint']}
        </p>
      </div>
    `;
  }
}
